import { useCallback } from "react";

import { useCustomMutation } from "@/hooks/use-graphql-client";

import { START_GUIDE_QUERY } from "@/components/features/_shared/repository/start-guide/query/schema";
import { RoomList_ListPage_Query } from "@/components/features/items/list/room/_hooks/use-room-list/query";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { DummyRoomCreateMutation } from "./mutation";
import { viewModelToDummyRoomInputConverter } from "../../_common/view-model-to-dummy-room-input";
import { DummyRoomNewItem_EditPage_Query } from "../query/query";

import type { ProductDummyRoomFormModel } from "../../../type";

type Props = {
	resetForm: () => void;

	onCreateRoomSuccessCallback: () => void;
};

export const useDummyRoomEditMutation = ({ resetForm, onCreateRoomSuccessCallback }: Props) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const { mutationFunc } = useCustomMutation(DummyRoomCreateMutation);

	const onUpdate = useCallback(
		async (
			viewModel: ProductDummyRoomFormModel["ViewModel"], //
		) => {
			showLoadingScreenCallback();

			const input = viewModelToDummyRoomInputConverter({ viewModel });

			const { errors } = await mutationFunc({
				variables: {
					input,
				},
				refetchQueries: [
					{
						query: RoomList_ListPage_Query,
						variables: {
							hotelID: input.hotelID,
						},
					},
					{
						query: DummyRoomNewItem_EditPage_Query,
						variables: {
							hotelID: input.hotelID,
						},
						fetchPolicy: "no-cache",
					},
					{
						query: START_GUIDE_QUERY,
						variables: { hotelID: input.hotelID },
					},
				],
			}).finally(hideLoadingScreenCallback);

			if (!errors) {
				notifyUserEventInfo({
					title: "ダミー部屋を作成しました",
				});

				resetForm();
				onCreateRoomSuccessCallback();
			}
		},
		[hideLoadingScreenCallback, mutationFunc, onCreateRoomSuccessCallback, resetForm, showLoadingScreenCallback],
	);

	return { onUpdate };
};
